import React, { useState } from 'react';

interface StepResult {
  success: boolean;
  data?: any;
  error?: string;
  timestamp: number;
}

interface FlowStep {
  id: string;
  title: string;
  action: () => Promise<StepResult>;
  status: 'waiting' | 'running' | 'success' | 'error';
  result?: StepResult;
}

export default function LiveBettingFlow() {
  const [currentStep, setCurrentStep] = useState(-1);
  const [isRunning, setIsRunning] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [betAmount, setBetAmount] = useState('');
  const [selectedOption, setSelectedOption] = useState<'YES' | 'NO' | null>(null);
  const [betData, setBetData] = useState<any>(null);

  const getSteps = (): FlowStep[] => [
    {
      id: 'open-modal',
      title: '1. Click Place Bet → Modal Opens',
      status: 'waiting',
      action: async () => {
        setModalOpen(true);
        return {
          success: true,
          data: { modalOpen: true },
          timestamp: Date.now()
        };
      }
    },
    {
      id: 'select-option',
      title: '2. Select YES or NO',
      status: 'waiting',
      action: async () => {
        return new Promise((resolve) => {
          const checkInterval = setInterval(() => {
            if (selectedOption) {
              clearInterval(checkInterval);
              resolve({
                success: true,
                data: { option: selectedOption },
                timestamp: Date.now()
              });
            }
          }, 100);

          setTimeout(() => {
            clearInterval(checkInterval);
            if (!selectedOption) {
              resolve({
                success: false,
                error: 'No option selected after 10 seconds',
                timestamp: Date.now()
              });
            }
          }, 10000);
        });
      }
    },
    {
      id: 'enter-amount',
      title: '3. Enter Bet Amount',
      status: 'waiting',
      action: async () => {
        return new Promise((resolve) => {
          const checkInterval = setInterval(() => {
            if (betAmount && parseFloat(betAmount) > 0) {
              clearInterval(checkInterval);
              resolve({
                success: true,
                data: { amount: parseFloat(betAmount) },
                timestamp: Date.now()
              });
            }
          }, 100);

          setTimeout(() => {
            clearInterval(checkInterval);
            if (!betAmount) {
              resolve({
                success: false,
                error: 'No amount entered after 10 seconds',
                timestamp: Date.now()
              });
            }
          }, 10000);
        });
      }
    },
    {
      id: 'calculate',
      title: '4. Calculate Probability & Payout',
      status: 'waiting',
      action: async () => {
        const amount = parseFloat(betAmount);
        const currentProb = selectedOption === 'YES' ? 0.75 : 0.25;
        const k = 100;
        const newProb = (currentProb * k + amount) / (k + amount);
        const shares = amount / currentProb;
        const potentialPayout = shares * 1;

        const calculation = {
          amount,
          option: selectedOption,
          currentProbability: currentProb,
          newProbability: newProb,
          probabilityChange: newProb - currentProb,
          shares: shares.toFixed(2),
          potentialPayout: potentialPayout.toFixed(2)
        };

        setBetData(calculation);

        return {
          success: true,
          data: calculation,
          timestamp: Date.now()
        };
      }
    },
    {
      id: 'confirm',
      title: '5. Confirm & Submit Bet',
      status: 'waiting',
      action: async () => {
        await new Promise(resolve => setTimeout(resolve, 500));

        const bet = {
          id: `bet-${Date.now()}`,
          marketId: 'test-market',
          userId: 'test-user',
          option: selectedOption,
          amount: parseFloat(betAmount),
          timestamp: Date.now(),
          ...betData
        };

        return {
          success: true,
          data: bet,
          timestamp: Date.now()
        };
      }
    },
    {
      id: 'update-ui',
      title: '6. Update Balance & Market',
      status: 'waiting',
      action: async () => {
        await new Promise(resolve => setTimeout(resolve, 300));

        return {
          success: true,
          data: {
            newBalance: 1000 - parseFloat(betAmount),
            marketUpdated: true,
            probability: betData?.newProbability
          },
          timestamp: Date.now()
        };
      }
    }
  ];

  const [steps, setSteps] = useState<FlowStep[]>(getSteps());

  const runNextStep = async () => {
    const nextStepIndex = currentStep + 1;
    const currentSteps = getSteps();

    if (nextStepIndex >= currentSteps.length) {
      setIsRunning(false);
      return;
    }

    setCurrentStep(nextStepIndex);
    const step = currentSteps[nextStepIndex];

    const updatedSteps = [...currentSteps];
    updatedSteps[nextStepIndex] = { ...step, status: 'running' };
    setSteps(updatedSteps);

    const result = await step.action();

    updatedSteps[nextStepIndex] = {
      ...step,
      status: result.success ? 'success' : 'error',
      result
    };
    setSteps(updatedSteps);

    if (result.success) {
      if (nextStepIndex < currentSteps.length - 1) {
        setTimeout(() => runNextStep(), 1000);
      } else {
        setIsRunning(false);
      }
    } else {
      setIsRunning(false);
    }
  };

  const startFlow = () => {
    setCurrentStep(-1);
    setIsRunning(true);
    setModalOpen(false);
    setBetAmount('');
    setSelectedOption(null);
    setBetData(null);

    setSteps(getSteps().map(s => ({ ...s, status: 'waiting', result: undefined })));

    setTimeout(() => runNextStep(), 500);
  };

  const getStepColor = (status: string) => {
    switch (status) {
      case 'success': return 'bg-green-500';
      case 'running': return 'bg-blue-500 animate-pulse';
      case 'error': return 'bg-red-500';
      default: return 'bg-gray-700';
    }
  };

  const getStepIcon = (status: string) => {
    switch (status) {
      case 'success': return '✅';
      case 'running': return '⏳';
      case 'error': return '❌';
      default: return '⭕';
    }
  };

  return (
    <div className="min-h-screen bg-gray-900 text-white p-8">
      <div className="max-w-6xl mx-auto">
        <h1 className="text-3xl font-bold mb-2">🎯 CYE: Live Betting Flow</h1>
        <p className="text-gray-400 mb-8">Can You Even... make ONE bet work? Watch every step execute in real-time.</p>

        <div className="grid md:grid-cols-2 gap-8">
          <div className="space-y-4">
            <button
              onClick={startFlow}
              disabled={isRunning}
              className={`w-full py-3 rounded-lg font-bold ${
                isRunning
                  ? 'bg-gray-700 cursor-not-allowed'
                  : 'bg-gradient-to-r from-green-500 to-blue-500 hover:from-green-600 hover:to-blue-600'
              }`}
            >
              {isRunning ? '🔄 Flow Running...' : '🚀 Start Live Flow'}
            </button>

            <div className="bg-gray-800 rounded-lg p-6">
              <h2 className="font-bold mb-4">📊 Flow Steps</h2>
              <div className="space-y-3">
                {steps.map((step, index) => (
                  <div
                    key={step.id}
                    className={`p-3 rounded-lg ${getStepColor(step.status)} transition-all duration-500 ${
                      step.status === 'running' ? 'scale-105' : ''
                    }`}
                  >
                    <div className="flex items-center gap-2">
                      <span className="text-xl">{getStepIcon(step.status)}</span>
                      <div className="flex-1">
                        <div className="font-semibold">{step.title}</div>
                        {step.result && (
                          <div className="text-xs mt-1 font-mono">
                            {step.result.success ? (
                              <span className="text-green-200">
                                ✓ {JSON.stringify(step.result.data, null, 2)}
                              </span>
                            ) : (
                              <span className="text-red-200">
                                ✗ {step.result.error}
                              </span>
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          <div className="space-y-4">
            <div className="bg-gray-800 rounded-lg p-6">
              <h2 className="font-bold mb-4">🎮 Interactive Area</h2>

              {modalOpen && (
                <div className="bg-gray-700 rounded-lg p-4 mb-4 animate-slideIn">
                  <h3 className="font-bold mb-3">Place Your Bet</h3>

                  <div className="mb-4">
                    <label className="block text-sm mb-2">Select Option:</label>
                    <div className="flex gap-2">
                      <button
                        onClick={() => setSelectedOption('YES')}
                        className={`flex-1 py-2 rounded ${
                          selectedOption === 'YES'
                            ? 'bg-green-500'
                            : 'bg-gray-600 hover:bg-gray-500'
                        }`}
                      >
                        YES
                      </button>
                      <button
                        onClick={() => setSelectedOption('NO')}
                        className={`flex-1 py-2 rounded ${
                          selectedOption === 'NO'
                            ? 'bg-red-500'
                            : 'bg-gray-600 hover:bg-gray-500'
                        }`}
                      >
                        NO
                      </button>
                    </div>
                  </div>

                  <div className="mb-4">
                    <label className="block text-sm mb-2">Bet Amount:</label>
                    <input
                      type="number"
                      value={betAmount}
                      onChange={(e) => setBetAmount(e.target.value)}
                      className="w-full px-3 py-2 bg-gray-600 rounded"
                      placeholder="Enter amount..."
                      min="1"
                      max="1000"
                    />
                  </div>

                  {betData && (
                    <div className="bg-gray-800 rounded p-3 text-sm">
                      <div>Shares: {betData.shares}</div>
                      <div>New Probability: {(betData.newProbability * 100).toFixed(1)}%</div>
                      <div>Potential Payout: ${betData.potentialPayout}</div>
                    </div>
                  )}
                </div>
              )}

              {!modalOpen && currentStep === -1 && (
                <div className="text-center py-8 text-gray-500">
                  Click "Start Live Flow" to begin
                </div>
              )}

              {currentStep >= 0 && (
                <div className="bg-gray-700 rounded p-4">
                  <h4 className="font-bold mb-2">Current State:</h4>
                  <pre className="text-xs overflow-auto">
{JSON.stringify({
  step: currentStep + 1,
  modalOpen,
  selectedOption,
  betAmount,
  betData
}, null, 2)}
                  </pre>
                </div>
              )}
            </div>

            <div className="bg-blue-900/20 border border-blue-500/30 rounded-lg p-4">
              <h3 className="font-bold text-blue-400 mb-2">💡 What's Happening</h3>
              <p className="text-sm text-gray-300">
                This component shows EXACTLY what happens when you place a bet.
                Each step executes in sequence, with real state changes and visual feedback.
                You can interact with the modal when it opens - the flow waits for your input!
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}